import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { ApiException } from '../../../../../../../types';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import {
  GPanelContactsKeys,
  restoreLabels,
  restoreSync,
} from '../../../gpanel-contacts-api';
import { useStore } from '../../../../../state/zustand-store';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { Messages } from '../../../../../../utils/message-constants';
import { getSyncsByLabelId } from '../../../gpanel-contacts-api';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { Label, LabelGroupsResponse, LabelSync } from '../../../shared/types';

import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

export default function GpanelContactsRecoverLabelsDialog({
  handleClose,
  labelIds,
}: {
  handleClose: () => void;
  labelIds: number[];
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const setSnackbar = useStore((state) => state.setSnackbar);
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();
  const [restoreSyncEnabled, setRestoreSyncEnabled] = useState(false);
  const [currentStep, setCurrentStep] = useState<'labels' | 'syncs' | null>(
    null
  );
  const [syncRestoreHadFailures, setSyncRestoreHadFailures] = useState(false);

  // Check if any of the labels have active sync configurations
  const trashedLabels =
    queryClient.getQueryData<Label[]>([GPanelContactsKeys.TrashLabels]) ?? [];

  const selectedLabel =
    labelIds.length === 1
      ? trashedLabels.find((l) => l.id === labelIds[0])
      : null;

  const { data: allLabelSyncs = [] } = useQuery({
    queryKey: [GPanelContactsKeys.LabelSyncs, ...labelIds],
    queryFn: async () => {
      const syncPromises = labelIds.map((labelId) =>
        getSyncsByLabelId(labelId.toString())
      );
      const syncResults = await Promise.all(syncPromises);
      return syncResults.flat();
    },
    enabled: labelIds.length > 0,
  });

  // Check if any label has active syncs
  const hasActiveSync = allLabelSyncs.length > 0;

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      setCurrentStep('labels');
      setSyncRestoreHadFailures(false);
      // First restore the labels
      await restoreLabels(labelIds, changeLogMessage);

      // If restore sync is checked, restore all disconnected syncs for these labels
      if (restoreSyncEnabled) {
        setCurrentStep('syncs');
        const disconnectedSyncs = allLabelSyncs.filter(
          (sync) => sync.status === 'DISCONNECTED'
        );

        if (disconnectedSyncs.length > 0) {
          try {
            const restoreResults = await Promise.allSettled(
              disconnectedSyncs.map((sync) => restoreSync(sync.id))
            );

            const failedCount = restoreResults.filter(
              (result) => result.status === 'rejected'
            ).length;

            const successCount = restoreResults.filter(
              (result) => result.status === 'fulfilled'
            ).length;

            if (failedCount > 0) {
              setSyncRestoreHadFailures(true);

              if (successCount === 0) {
                // All syncs failed
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: `Labels restored successfully, but all ${failedCount} sync${
                    failedCount !== 1 ? 's' : ''
                  } failed to restore. Please try restoring syncs manually.`,
                });
              } else {
                // Partial failure
                setSnackbar({
                  open: true,
                  severity: 'warning',
                  message: `Labels restored successfully. ${successCount} sync${
                    successCount !== 1 ? 's' : ''
                  } restored, ${failedCount} sync${
                    failedCount !== 1 ? 's' : ''
                  } failed. Please try restoring failed syncs manually.`,
                });
              }
            }
          } catch {
            // Unexpected error during sync restoration process
            setSyncRestoreHadFailures(true);
            setSnackbar({
              open: true,
              severity: 'error',
              message:
                'Labels restored successfully, but sync restoration failed due to an unexpected error. Please try restoring syncs manually.',
            });
          }
        }
      }
      setCurrentStep(null);
    },
    onSuccess: () => {
      setChangeLogMessage('');

      // Only show success message if no sync failures occurred
      if (!syncRestoreHadFailures) {
        const disconnectedSyncsCount = allLabelSyncs.filter(
          (sync) => sync.status === 'DISCONNECTED'
        ).length;
        let successMessage = Messages.Success.Restored(
          `Label${labelIds.length > 1 ? 's' : ''}`
        );

        if (restoreSyncEnabled && disconnectedSyncsCount > 0) {
          successMessage += ` and ${disconnectedSyncsCount} sync${
            disconnectedSyncsCount > 1 ? 's' : ''
          }`;
        }

        setSnackbar({
          open: true,
          severity: 'success',
          message: successMessage,
        });
      }

      if (restoreSyncEnabled) {
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const [firstKey] = query.queryKey;
              return firstKey === GPanelContactsKeys.LabelSyncs;
            },
          },
          (oldData: LabelSync[]) => {
            if (!oldData || !Array.isArray(oldData)) return oldData;
            const updatedData = oldData.map((sync: LabelSync) => {
              if (labelIds.includes(sync.labelId)) {
                return {
                  ...sync,
                  deleted: false,
                  status: 'PENDING',
                };
              }
              return sync;
            });

            return updatedData;
          }
        );
      }
      const labelsToRecoverFromCache =
        queryClient
          .getQueryData<Label[]>([GPanelContactsKeys.TrashLabels])
          ?.filter((l) => labelIds.includes(l.id)) ?? [];

      queryClient.setQueryData<LabelGroupsResponse>(
        [GPanelContactsKeys.LabelGroups],
        (oldData) => {
          if (!oldData) return oldData;
          const recoveredLabels = labelsToRecoverFromCache.map((l) => ({
            ...l,
            trashed: false,
          }));
          return {
            ...oldData,
            labelGroups: oldData.labelGroups.map((group) => ({
              ...group,
              labels: [...group.labels, ...recoveredLabels],
            })),
          };
        }
      );
      queryClient.setQueryData<Label[]>(
        [GPanelContactsKeys.TrashLabels],
        (oldData) => oldData?.filter((l) => !labelIds.includes(l.id)) ?? []
      );

      queryClient.setQueryData<Label[]>(
        [GPanelContactsKeys.GPanelLabels],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((l) => {
            if (labelIds.includes(l.id)) {
              return { ...l, trashed: false };
            }
            return l;
          });
        }
      );

      if (restoreSyncEnabled) {
        setTimeout(() => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const [firstKey] = query.queryKey;
              return firstKey === GPanelContactsKeys.LabelSyncs;
            },
          });
        }, 2000); // Wait 2 seconds for backend processing
      }

      handleClose();
    },
    onError: (error) => {
      setCurrentStep(null);
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Restoring(
              currentStep === 'syncs' ? 'syncs' : 'labels'
            ),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(
        analytics.directory.gpanel_contacts.restore_from_label_trash,
        {
          module: ModuleList.modules.directory.name,
          section: ModuleList.modules.directory.sections.gpanelContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.RecoverTrashedLabels.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const labelSuffix = labelIds.length > 1 ? 's' : '';

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title={`Restore Label${labelSuffix}`}
        dialogContent={
          <>
            <Typography sx={{ mb: 2 }}>
              {labelIds.length > 1 ? (
                <>
                  This will restore <strong>{labelIds.length}</strong> labels
                  and re-link all contacts that previously belonged to them.
                </>
              ) : (
                <>
                  This will restore <strong>{selectedLabel?.name}</strong> and
                  re-link all contacts that previously belonged to it.
                </>
              )}
            </Typography>
            {hasActiveSync && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                One or more of the labels have contact syncs associated with it.
                They will be recovered and will need to be manually restarted to
                restore the synchronization process. You can automatically
                restore these syncs by checking below.
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={restoreSyncEnabled}
                      onChange={(e) => setRestoreSyncEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Automatically restore all disconnected syncs.
                    </Typography>
                  }
                />
              </Alert>
            )}
            {errorText && <FormHelperText error children={errorText} />}
          </>
        }
        primaryButton={{
          title: isPending
            ? currentStep === 'labels'
              ? 'Restoring Labels...'
              : currentStep === 'syncs'
              ? 'Restoring Syncs...'
              : `Restore Label${labelSuffix}`
            : `Restore Label${labelSuffix}`,
          color: 'primary',
          action: handleSubmit,
          loading: isPending,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
